<template>
  <div class="prose prose-lg max-w-none">
    <p class="text-gray-600 mb-6">Last updated: 11 October 2026</p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. The Bindle Platform</h2>
      <p class="text-gray-700 mb-4">
        Bindle is an online learning platform that enables students to access educational services provided by independent tutors. Tutors offer weekly one-to-one tutoring sessions, together with their own recorded educational materials.
      </p>
      <p class="text-gray-700 mb-4">
        Tutors operate as independent service providers. Bindle provides the platform infrastructure, scheduling and payment processing but does not directly provide tutoring services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Student Accounts</h2>
      <p class="text-gray-700 mb-4">
        To access Bindle services, students must create an account and provide accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.
      </p>
      <p class="text-gray-700 mb-4">
        Bindle reserves the right to suspend or terminate accounts that violate these Terms or misuse the platform.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Student Age Requirements</h2>
      <p class="text-gray-700 mb-4">
        Bindle may be used by students under the age of 18. However, if you are under 18, a parent or legal guardian must review and accept these Terms on your behalf.
      </p>
      <p class="text-gray-700 mb-4">
        By creating an account or using the platform, you confirm that either:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>you are at least 18 years old, or</li>
        <li>a parent or legal guardian has accepted these Terms and agrees to be responsible for your use of the platform.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Bindle does not knowingly enter into contracts directly with individuals under the age of 18 without parental or guardian consent.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Tutor Subscriptions</h2>
      <p class="text-gray-700 mb-4">
        Tutors offer subscription-based learning services through the Bindle platform. A subscription is a standing arrangement with a tutor for a weekly one-to-one tutoring session at an agreed time, together with access to that tutor's recorded educational content.
      </p>
      <p class="text-gray-700 mb-4">
        The price of each session is set by the tutor and displayed on the tutor's page prior to purchase. By subscribing, you agree to that price for every session under the subscription, extra sessions included; a later change to the tutor's price does not affect your subscription. A subscription starts only once the tutor accepts your request, and its first session is at least 48 hours after you requested it: a weekly time that comes round sooner starts the following week.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Payments</h2>
      <p class="text-gray-700 mb-4">
        Payments are processed securely through third-party payment providers. By subscribing, you authorise Bindle to charge your nominated payment method for each session until the subscription ends.
      </p>
      <p class="text-gray-700 mb-4">
        When you request a subscription, the price of the first session is reserved on your payment method. It is taken only if the tutor accepts, and released if they decline or do not respond. If the subscription ends before that first session has been booked, the payment is refunded in full.
      </p>
      <p class="text-gray-700 mb-4">
        Every later session is charged individually, 48 hours before it starts, without you needing to be present. We email you a receipt for each payment.
      </p>
      <p class="text-gray-700 mb-4">
        A session agreed less than 48 hours before it starts, such as an extra session or one moved to a sooner time, is paid for when it is agreed. When you ask for one, or agree to one your tutor has offered, you confirm the payment then; the amount is reserved on your payment method, taken when the session is agreed, and released if it is not.
      </p>
      <p class="text-gray-700 mb-4">
        If a payment fails, we will email you and the session stays booked. You can pay for it from the billing page of your account, including with a different card, and must do so if your bank asks you to confirm the payment. If your bank declined the payment for lack of funds, we will also try again every 12 hours, and every 6 hours if it declined it without giving a reason. A session whose payment failed still goes ahead as booked, and its price remains owed to Bindle: we keep trying for 7 days after it, and you can still pay for it from the billing page. You may cancel a session whose payment failed at any time before it starts, without charge.
      </p>
      <p class="text-gray-700 mb-4">
        Any credit on your account is used towards the next session we charge you for, before your payment method is charged. It is not used for the first session of a new subscription, or for a session paid for when it is agreed, which are paid from the amount reserved. Credit does not expire and cannot be exchanged for cash. It stays on your account if your subscriptions end, and is used if you book again.
      </p>
      <p class="text-gray-700 mb-4">
        If a payment is taken for a session that no longer needs it, for example because it was cancelled at the same moment or was already paid for, it is refunded in full automatically.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Cancellation</h2>
      <p class="text-gray-700 mb-4">
        You may cancel a single session, or end a subscription, at any time through your Bindle account. The cost of cancelling a session is shown before you confirm:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>more than 48 hours before the session, there is no charge, and anything already paid for it is refunded, and</li>
        <li>within 48 hours of the session, the full session price is charged. If the session has not been paid for yet, it is charged when you cancel; if that payment fails, the amount remains owed to Bindle. This does not apply to a session whose payment had already failed, which is cancelled without charge.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        The 48 hours are counted from the time the session was due to be charged. A session charged and then moved to a later time is still charged in full if you cancel it.
      </p>
      <p class="text-gray-700 mb-4">
        Ending a subscription takes effect immediately. No further sessions are charged, and any session within 48 hours, or already paid for, is cancelled on the terms above. A session that has already started and is not yet paid for remains owed.
      </p>
      <p class="text-gray-700 mb-4">
        If a tutor cancels a session, or ends the subscription, you are refunded in full for any session already paid for, and your tutor tells you why. If they do so less than 48 hours before the session, Bindle also adds £5 of credit to your account, unless your payment for the session had failed. If a tutor leaves Bindle or can no longer be paid through the platform, Bindle may end the subscription on the same terms.
      </p>
      <p class="text-gray-700 mb-4">
        Refunds go back to the payment method used and usually take 5 to 10 days to appear. Any part of a session paid with credit is returned as credit.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Scheduling and Attendance</h2>
      <p class="text-gray-700 mb-4">
        Tutors determine their availability and publish scheduled sessions through the platform. Students book a recurring weekly session with a tutor at a time both have agreed.
      </p>
      <p class="text-gray-700 mb-4">
        Moving a session, adding an extra session or changing the weekly time is a request that the other party must accept. A request that is not answered in time lapses, and the session stays as it was. A change to the weekly time does not move sessions already paid for.
      </p>
      <p class="text-gray-700 mb-4">
        You may pause a subscription, choosing the day it starts again, for up to 4 weeks at a time and up to 8 weeks in any 12 months. No sessions take place or are charged while it is paused, your weekly time is kept for you, and it resumes automatically on the day you chose; we email you when it does. A session already paid for, or within 48 hours, still goes ahead and is charged as usual, and the pause starts the day after it. Once a pause is arranged, sessions cannot be moved or added, nor the weekly time changed, until it ends.
      </p>
      <p class="text-gray-700 mb-4">
        Students are responsible for joining sessions at the scheduled time using the platform link provided; we email a reminder with the link about 15 minutes before each session. Attendance is recorded by the platform's lesson room, and a session counts as delivered when the tutor is in the room within the first 15 minutes of the session and you join it, even late, or when the tutor joins later, within its first half, while you are in the room. Where that is not the case and it is not clear who missed the session, Bindle reviews it and decides whether it took place.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Missed Student Sessions</h2>
      <p class="text-gray-700 mb-4">
        If a student fails to attend a scheduled tutoring session while the tutor attends on time, the session will be considered missed. Missed sessions due to student absence are charged in full and are not eligible for refunds, credits or rescheduling. If your tutor agrees to refund a missed session, Bindle refunds it at their request.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. Tutor Non-Attendance</h2>
      <p class="text-gray-700 mb-4">
        If a tutor fails to attend a scheduled tutoring session, the session is refunded in full, the tutor is not paid for it, and Bindle adds £5 of credit to your account. Where the lesson room shows you attended and the tutor did not, this happens automatically about a day after the session, and we email you to confirm both. If your payment for the session had failed, you are no longer charged for it instead, and no credit is added.
      </p>
      <p class="text-gray-700 mb-4">
        Where attendance cannot be established from the lesson room, Bindle reviews the session before any payment is released. If something went wrong with a session — the tutor did not attend or was late, or you could not get in — report it from your account within 7 days. A session not yet paid for can be reported once it is paid, or by contacting us. If the tutor has not been paid for the session yet, they are not paid until Bindle has looked into it, and we email you our decision, including any refund.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Educational Content and Intellectual Property</h2>
      <p class="text-gray-700 mb-4">
        Educational materials provided through the platform, including recorded lessons, documents and resources, are provided for personal educational use only.
      </p>
      <p class="text-gray-700 mb-4">
        Students must not reproduce, distribute, record, sell, upload, or share any tutor content without permission. All intellectual property rights remain with the tutor or Bindle where applicable.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Acceptable Use</h2>
      <p class="text-gray-700 mb-4">
        Students must use the platform respectfully and in accordance with applicable law. Students must not:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>harass tutors or other users,</li>
        <li>share inappropriate or offensive content,</li>
        <li>disrupt lessons,</li>
        <li>attempt to bypass the platform by arranging off-platform tutoring payments, or</li>
        <li>misuse the platform in any way that interferes with its operation.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Bindle reserves the right to suspend or terminate accounts that breach these rules.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Platform Availability</h2>
      <p class="text-gray-700 mb-4">
        Bindle aims to maintain reliable access to the platform but does not guarantee uninterrupted service. The platform may occasionally be unavailable due to maintenance, technical issues, or third-party service disruptions.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">13. Refund Policy</h2>
      <p class="text-gray-700 mb-4">
        Except as expressly provided in Section 6 (Cancellation) and Section 9 (Tutor Non-Attendance), session payments are non-refundable. Bindle may issue refunds or adjustments at its discretion where appropriate. Refunds issued through the platform will be processed using the original payment method where possible.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">14. Limitation of Liability</h2>
      <p class="text-gray-700 mb-4">
        To the maximum extent permitted by law, Bindle shall not be liable for:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>academic performance outcomes,</li>
        <li>errors or omissions in tutor-provided content,</li>
        <li>interruptions caused by internet connectivity or third-party software, or</li>
        <li>indirect or consequential losses.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Nothing in these Terms limits liability where such limitation is not permitted by law.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">15. Governing Law</h2>
      <p class="text-gray-700 mb-4">
        These Terms are governed by the laws of England and Wales. Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
      </p>
    </section>
  </div>
</template>
